import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import { setSelectedNav } from '@actions/educator';
import H6 from '@base/H6/H6';
import LearnOnTheGo from '@comp/LearnOnTheGo/LearnOnTheGo';
import CustomModal from '@comp/CustomModal/CustomModal';
import H4 from '@base/H4/H4';
import Link from '@base/Link/Link';
import ChevronRight from '@icon/ChevronRight';
import { Spacings, BgColors, ShadowColors, TextColors } from '@styles/index';
import BaseFooter from '../Footer/BaseFooter/Footer';

const EduNavBarWrapper = styled.div`
  position: relative;
  grid-column: 1/6;
  z-index: 2;
`;

const EduNavBarFixed = styled.div`
  position: sticky;
  width: 225px;
  top: ${Spacings.SPACING_HEADER + Spacings.SPACING_HEADER_MARGIN}px;
`;

const Selected = styled.div`
  height: ${({ selectedElHeight }) => selectedElHeight}px;
  width: 100%;
  position: absolute;
  background-color: ${BgColors.WHITE};
  top: ${({ selectedElPos, selectedElHeight }) =>
    selectedElPos + (selectedElHeight > 40 ? 10 : 5)}px;
  transition: all 400ms ease-in;
  transition-property: top, height;
  border-radius: 8px;
  box-shadow: 0px 4px 16px ${ShadowColors.PRIMARY_SHADOW};
`;

const NavItems = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  & > :last-child {
    margin-top: 140px;
  }
`;

const MainItem = styled(H4)`
  padding: 8px 16px;
  transition: color 400ms ease-in-out;
  color: ${({ selected }) => selected && TextColors.BLUE};
  :hover {
    color: ${({ disabled }) => (disabled ? 'inherit' : TextColors.BLUE)};
  }
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const Item = styled(H6)`
  padding: 8px 16px;
  transition: color 400ms ease-in-out;
  color: ${({ selected }) => selected && TextColors.BLUE};
  font-weight: ${({ selected }) => (selected ? 600 : 'normal')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  :hover {
    color: ${({ disabled }) => (disabled ? TextColors.BLACK : TextColors.BLUE)};
  }
`;

const MenuContainer = styled.div`
  margin-bottom: 24px;
`;

const Others = styled.div`
  position: fixed;
  bottom: ${Spacings.SPACING_8B};
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EduNavBar = ({ display }) => {
  const mapStateToProps = ({ auth }) => ({
    auth
  });

  const { auth } = useSelectorToStore(mapStateToProps);
  const selectedNav = auth.selectedEduNav;
  const isReviewer = auth.accessControl?.is_reviewer;
  const isEligibleForDoubts = auth.accessControl?.is_doubt_topology_present;
  const isEvaluator = auth.accessControl?.is_evaluator;
  const isDriveEnabled = auth.accessControl?.is_drive_enabled;
  const isExternalContentUploader =
    auth.accessControl?.is_external_content_uploader;
  const { canAddPlusCourse, canAddSpecialClass, isEducator } = auth.me;

  const [selectedElHeight, setselectedElHeight] = useState(0);
  const [selectedElPos, setselectedElPos] = useState(0);
  const dispatch = useDispatchToStore();

  const handleClick = (nav) => () => dispatch(setSelectedNav(nav));
  const [openOtherLinks, setOpenOtherLinks] = useState(false);
  const closeOtherLinksModal = () => setOpenOtherLinks(false);
  const openOtherLinksModal = () => setOpenOtherLinks(true);
  const [openLearnOnTheGo, setOpenLearnOnTheGo] = useState(false);
  const closeLearnOnTheGo = () => setOpenLearnOnTheGo(false);
  const openLearnOnTheGoModal = () => setOpenLearnOnTheGo(true);

  useEffect(() => {
    const el = document.querySelector(`[data-id="${selectedNav}"]`);
    const selectedElemHeight = el?.offsetHeight;
    const selectedElemOffset = el?.offsetTop;
    setselectedElHeight(selectedElemHeight);
    setselectedElPos(selectedElemOffset - (selectedElemHeight > 40 ? 10 : 6));
  }, [selectedNav]);

  if (!display) return null;

  return (
    <EduNavBarWrapper>
      <EduNavBarFixed>
        <Selected
          selectedElPos={selectedElPos}
          selectedElHeight={selectedElHeight}
        />
        <NavItems>
          <MenuContainer>
            {isEducator && (
              <>
                <Link
                  href="/educator"
                  as="/educator"
                  onClick={handleClick('dashboard')}
                >
                  <MainItem
                    selected={selectedNav === 'dashboard'}
                    data-id="dashboard"
                  >
                    Dashboard
                  </MainItem>
                </Link>
                <Link
                  href="/educator/schedule"
                  as="/educator/schedule"
                  onClick={handleClick('eduSchedule')}
                >
                  <Item
                    selected={selectedNav === 'eduSchedule'}
                    data-id="eduSchedule"
                  >
                    My schedule
                  </Item>
                </Link>
              </>
            )}
            {canAddPlusCourse && (
              <Link
                href="/educator/courses/[type]"
                as="/educator/courses/plus-courses"
                onClick={handleClick('plus-courses')}
              >
                <Item
                  selected={selectedNav === 'plus-courses'}
                  data-id="plus-courses"
                >
                  Plus courses
                </Item>
              </Link>
            )}
            {canAddSpecialClass && (
              <Link
                href="/educator/courses/[type]"
                as="/educator/courses/special-classes"
                onClick={handleClick('special-classes')}
              >
                <Item
                  selected={selectedNav === 'special-classes'}
                  data-id="special-classes"
                >
                  Free live classes
                </Item>
              </Link>
            )}
            <Link
              href="/educator/courses/[type]"
              as="/educator/courses/free-courses"
              onClick={handleClick('free-courses')}
            >
              <Item
                selected={selectedNav === 'free-courses'}
                data-id="free-courses"
              >
                Free courses
              </Item>
            </Link>
            {isEligibleForDoubts && (
              <Link
                href="/educator/doubts"
                as="/educator/doubts"
                onClick={handleClick('doubts')}
              >
                <Item selected={selectedNav === 'doubts'} data-id="doubts">
                  Doubts
                </Item>
              </Link>
            )}
            {isReviewer && (
              <Link
                href="/educator/test/review"
                as="/educator/test/review"
                onClick={handleClick('reviewTest')}
              >
                <Item
                  selected={selectedNav === 'reviewTest'}
                  data-id="reviewTest"
                >
                  Review tests
                </Item>
              </Link>
            )}
            {isEvaluator && (
              <Link
                href="/educator/test/evaluate"
                as="/educator/test/evaluate"
                onClick={handleClick('testEval')}
              >
                <Item selected={selectedNav === 'testEval'} data-id="testEval">
                  Subjective tests
                </Item>
              </Link>
            )}
            {isDriveEnabled && (
              <Link
                href="/educator/drive"
                as="/educator/drive"
                onClick={handleClick('drive')}
              >
                <Item selected={selectedNav === 'drive'} data-id="drive">
                  Cloud
                </Item>
              </Link>
            )}
            {isExternalContentUploader && (
              <Link
                href="/educator/test/upload"
                as="/educator/test/upload"
                onClick={handleClick('uploadTests')}
              >
                <Item
                  selected={selectedNav === 'uploadTests'}
                  data-id="uploadTests"
                >
                  Upload Tests
                </Item>
              </Link>
            )}
          </MenuContainer>
          <Others>
            <>
              <LinkContainer>
                <Item
                  data-id="myLibrary"
                  selected={openLearnOnTheGo}
                  onClick={openLearnOnTheGoModal}
                >
                  Get the educator app
                </Item>
                <ChevronRight size="16px" color="#808080" />
              </LinkContainer>
              <LinkContainer>
                <Item selected={openOtherLinks} onClick={openOtherLinksModal}>
                  Other links
                </Item>
                <ChevronRight size="16px" color="#808080" />
              </LinkContainer>
            </>
          </Others>
        </NavItems>
      </EduNavBarFixed>
      <LearnOnTheGo
        onClose={closeLearnOnTheGo}
        modalState={openLearnOnTheGo}
        isEducator
        isFloating
      />
      <CustomModal
        overlayTransparent
        modalState={openOtherLinks}
        onClose={closeOtherLinksModal}
        bottomFrom="20px"
        bottomTo="40px"
        gridColumn="6/20"
      >
        <BaseFooter display floating isEducator />
      </CustomModal>
    </EduNavBarWrapper>
  );
};

export default EduNavBar;

EduNavBar.propTypes = {
  display: PropTypes.bool
};

EduNavBar.defaultProps = {
  display: false
};
